import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, EmptyState, Field, Input, Select, Spinner } from '../components/ui';
import { Icon, type IconName } from '../components/icons';
import { Notice } from '../components/Page';
import { Menu, MenuItem, MenuSeparator } from '../components/Menu';
import { useConfirm } from '../components/Dialog';
import { useToast } from '../components/Toast';
import { Pager } from '../components/Pager';
import { useActiveCompany } from '../components/CompanySwitcher';
import { useFolderParam } from '../lib/workspace';
import type { FormSummary } from '../lib/types';

/**
 * Rows per page.
 *
 * Sent on every request rather than left to the server's own default, which is still 25 for
 * API callers that ask for no particular size. Because it is always explicit, the two cannot
 * disagree about what page 2 contains — which is the only thing that mattered about them
 * matching.
 */
const PAGE_SIZE = 10;

const TYPE_VALUES: Record<string, number> = {
  Survey: 1, Quiz: 2, Poll: 3, Application: 4, Assessment: 5, DigitalForm: 6,
};

/**
 * The filter offers exactly what this studio can create, from the same map the create
 * panel uses, so the two cannot drift into offering different sets. The server accepts
 * any name its enum knows, so a type made through the API is still reachable by hand.
 */
const TYPE_LABEL: Record<string, string> = {
  Survey: 'Survey',
  Quiz: 'Quiz',
  Poll: 'Poll',
  Application: 'Application',
  Assessment: 'Assessment',
  DigitalForm: 'Digital form',
};

/** Each form type gets a glyph so the list scans without reading. */
const TYPE_ICON: Record<string, IconName> = {
  Survey: 'paragraph',
  Quiz: 'check',
  Poll: 'gauge',
  Application: 'page',
  Assessment: 'star',
  DigitalForm: 'checkbox',
};

/** Shared with the response views, which date rows the same way. */
export const relative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

/**
 * The forms list.
 *
 * A dense table rather than a grid of cards. Cards gave every form the same weight and
 * fitted six to a screen; the questions here — which are live, which took a response, what
 * changed today — are comparisons down a column, which is what a table is for.
 *
 * Folders are not on this page any more. They are in the app rail, and the selection
 * arrives through the URL (see useFolderParam), so the same filter is visible and settable
 * from anywhere in the studio.
 */
export function FormList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [folder] = useFolderParam();
  const { active } = useActiveCompany();
  const confirm = useConfirm();
  const toast = useToast();

  // The command palette's "New form" lands here with ?new=1 rather than reaching into
  // this component's state.
  const creating = params.get('new') === '1';
  const setCreating = (value: boolean) => {
    const next = new URLSearchParams(params);
    if (value) next.set('new', '1');
    else next.delete('new');
    setParams(next, { replace: true });
  };

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // In the URL for the same reason as the folder: "all the quizzes in Customer research"
  // is a view worth linking to, and it survives a reload and the back button.
  const type = params.get('type');

  const setType = (next: string | null) => {
    const updated = new URLSearchParams(params);
    if (next) updated.set('type', next);
    else updated.delete('type');
    setParams(updated, { replace: true });
  };

  // In the URL, like the folder and the create panel: paging survives a reload, the back
  // button, and a link pasted to somebody else. Page 1 is the absence of the parameter
  // rather than "?page=1", so the ordinary URL stays clean.
  const page = Math.max(1, Math.trunc(Number(params.get('page'))) || 1);

  const setPage = (next: number) => {
    const updated = new URLSearchParams(params);
    if (next <= 1) updated.delete('page');
    else updated.set('page', String(next));
    setParams(updated, { replace: true });
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['forms', search, active?.id ?? 'all', folder ?? 'all', type ?? 'all', page],
    queryFn: () => api.forms.list({
      ...(search ? { q: search } : {}),
      ...(active ? { companyId: active.id } : {}),
      ...(folder ? { projectId: folder } : {}),
      ...(type ? { type } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),

    // Hold the current page on screen while the next one loads. Without this the table
    // empties and the footer jumps on every click, which reads as the list breaking.
    placeholderData: keepPreviousData,
  });

  // Narrowing a filter usually shortens the list, and staying on page 4 of a result that
  // now has one page shows nothing. The filters live in three different places - a text
  // box here, the company switcher, the app rail - so this is caught in one spot rather
  // than by remembering to reset the page in each of them.
  const filterKey = JSON.stringify([search, active?.id ?? null, folder ?? null, type ?? null]);
  const lastFilter = useRef(filterKey);

  useEffect(() => {
    if (lastFilter.current === filterKey) return;
    lastFilter.current = filterKey;
    if (page !== 1) setPage(1);
    // setPage closes over the current params; re-running on those is not wanted here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page]);

  // The server clamps a page past the end rather than returning nothing, so the URL is
  // corrected to whatever it actually served - otherwise the address bar keeps claiming a
  // page that does not exist.
  useEffect(() => {
    if (data && data.page !== page) setPage(data.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page]);

  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api.projects.list() });

  const fileForm = useMutation({
    mutationFn: (input: { formId: string; projectId: string | null }) =>
      api.projects.file(input.formId, input.projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
    },
  });

  const create = useMutation({
    mutationFn: (input: { name: string; type: number; workspaceId?: string }) =>
      api.forms.create(input),
    onSuccess: (form) => {
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      navigate(`/forms/${form.id}`);
    },
  });

  /** The form being renamed in place, if any. */
  const [renamingId, setRenamingId] = useState<string | null>(null);

  const invalidateForms = () => {
    void queryClient.invalidateQueries({ queryKey: ['forms'] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  const rename = useMutation({
    mutationFn: (input: { formId: string; name: string }) =>
      api.forms.rename(input.formId, { name: input.name }),
    onSuccess: () => { setRenamingId(null); invalidateForms(); },
    onError: (e: Error) => { setRenamingId(null); toast.error(e.message); },
  });

  const duplicate = useMutation({
    mutationFn: (formId: string) => api.forms.duplicate(formId),
    // The copy is a draft and the list sorts by last edited, so it arrives at the top.
    // Selecting it says which of two identically named rows is the new one.
    onSuccess: (copy) => {
      setSelectedId(copy.id);
      invalidateForms();
      toast.success(`Duplicated as “${copy.name}”.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (formId: string) => api.forms.remove(formId),
    onSuccess: () => { setSelectedId(null); invalidateForms(); toast.success('Form deleted.'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const forms = data?.data ?? [];

  // A selection that no longer exists — the filter changed, the form was deleted — must
  // not leave the detail pane describing something that is not on screen.
  useEffect(() => {
    if (selectedId && !forms.some((f) => f.id === selectedId)) setSelectedId(null);
  }, [forms, selectedId]);

  const selected = forms.find((f) => f.id === selectedId) ?? forms[0];
  const folderName = folder === 'none'
    ? 'Unfiled'
    : projects.data?.data.find((p) => p.id === folder)?.name;

  return (
    <div className="flex min-h-full">
      <div className="min-w-0 flex-1 pb-10">
        <header className="flex flex-wrap items-baseline gap-x-3 gap-y-2 px-6 pb-3 pt-[18px]">
          <h1 className="text-[21px] font-semibold leading-none tracking-[-0.01em]">
            {folderName ?? 'Forms'}
          </h1>
          {/* Says which filters are on, because two of the three are set from outside this
              header — the folder from the app rail, the company from the switcher. */}
          <p className="text-[13.5px] text-[var(--ff-text-muted)]">
            {[
              type ? `${TYPE_LABEL[type] ?? type}s` : null,
              search ? `matching “${search}”` : null,
            ].filter(Boolean).join(', ') || 'Sorted by last edited'}
          </p>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Select
              value={type ?? ''}
              onChange={(e) => setType(e.target.value || null)}
              aria-label="Filter by form type"
              className="h-8 w-[9.5rem] py-0 text-[13px]"
            >
              <option value="">All types</option>
              {Object.keys(TYPE_VALUES).map((t) => (
                <option key={t} value={t}>{TYPE_LABEL[t] ?? t}</option>
              ))}
            </Select>

            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter"
              aria-label="Filter forms"
              className="h-8 w-40"
            />
            <Button variant="primary" className="whitespace-nowrap"
                    onClick={() => setCreating(true)}>
              New form
            </Button>
          </div>
        </header>

        {error && (
          <div className="px-6 pb-3">
            <Notice tone="danger">Could not load forms. Check that the API is running.</Notice>
          </div>
        )}

        {creating && (
          <div className="px-6 pb-3">
            <CreateFormCard
              busy={create.isPending}
              onCancel={() => setCreating(false)}
              onCreate={(name, type, workspaceId) => create.mutate({ name, type, workspaceId })}
            />
          </div>
        )}

        {isLoading && <div className="p-8"><Spinner label="Loading forms" /></div>}

        {data && forms.length === 0 && !creating && (
          <EmptyState
            title={type
              ? `No ${(TYPE_LABEL[type] ?? type).toLowerCase()}s here`
              : folder ? 'Nothing in this folder' : search ? 'No forms match' : 'No forms yet'}
            description={type
              ? 'That is this filter, not an empty account — clear it to see everything else.'
              : folder
                ? 'Move a form in here from the panel on the right.'
                : 'Create your first form, add a few questions, then publish it and drop the '
                  + 'embed snippet into any page.'}
            action={type
              ? <Button onClick={() => setType(null)}>Show all types</Button>
              : <Button variant="primary" onClick={() => setCreating(true)}>Create a form</Button>}
          />
        )}

        {forms.length > 0 && (
          <>
            <div className="grid grid-cols-[1.6fr_74px_120px_88px_104px_32px] items-center gap-3.5
                            border-b border-[var(--ff-border)] px-6 pb-1.5 text-[11.5px]
                            text-[var(--ff-text-faint)]">
              <div>Form</div>
              <div className="hidden sm:block">Questions</div>
              <div>Responses</div>
              <div>Status</div>
              <div className="hidden sm:block">Edited</div>
            </div>

            <ul>
              {forms.map((form) => (
                <FormRow
                  key={form.id}
                  form={form}
                  renaming={renamingId === form.id}
                  onRename={() => setRenamingId(form.id)}
                  onCancelRename={() => setRenamingId(null)}
                  onRenamed={(value) => {
                    const trimmed = value.trim();
                    if (!trimmed || trimmed === form.name) setRenamingId(null);
                    else rename.mutate({ formId: form.id, name: trimmed });
                  }}
                  onDuplicate={() => duplicate.mutate(form.id)}
                  onDelete={async () => {
                    const ok = await confirm({
                      title: `Delete “${form.name}”?`,
                      tone: 'danger',
                      confirmLabel: 'Delete form',
                      body: form.responseCount > 0
                        ? `Its ${form.responseCount} response`
                          + `${form.responseCount === 1 ? ' stays' : 's stay'} recorded, and stop `
                          + 'being reachable from the studio.'
                        : 'The form stops being reachable from the studio.',
                    });

                    if (ok) remove.mutate(form.id);
                  }}
                  selected={form.id === selected?.id}
                  onSelect={() => setSelectedId(form.id)}
                  onOpen={() => navigate(`/forms/${form.id}`)}
                />
              ))}
            </ul>

            {/*
              Guarded on the paging fields, not just on `data`. The studio hot-reloads from
              source and the API does not, so a session can run new UI against an older
              build that answers without them — and `total.toLocaleString()` then took the
              whole list down with it. Defaulting the count to 0 would be worse: it would
              read "0 forms" above a screenful of them.
            */}
            {data && typeof data.total === 'number' && typeof data.totalPages === 'number' && (
              <Pager
                page={data.page ?? 1}
                totalPages={data.totalPages}
                total={data.total}
                noun="form"
                busy={isFetching}
                onChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {selected && (
        <DetailPane
          form={selected}
          folders={projects.data?.data ?? []}
          onFile={(projectId) => fileForm.mutate({ formId: selected.id, projectId })}
        />
      )}
    </div>
  );
}

function FormRow({
  form, selected, onSelect, onOpen, onDuplicate, onDelete,
  renaming, onRename, onRenamed, onCancelRename,
}: {
  form: FormSummary;
  selected: boolean;
  onSelect: () => void;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  renaming: boolean;
  onRename: () => void;
  onRenamed: (value: string) => void;
  onCancelRename: () => void;
}) {
  const live = form.status === 'Published';

  return (
    <li>
      {/*
        One row, two intents: click selects and shows the detail, double-click and Enter
        open the editor. A row that navigated on a single click would make the detail pane
        unreachable by mouse.
      */}
      <div
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        onClick={onSelect}
        onDoubleClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onOpen(); }
          if (e.key === ' ') { e.preventDefault(); onSelect(); }
        }}
        className={clsx(
          'group/row grid cursor-pointer grid-cols-[1.6fr_74px_120px_88px_104px_32px] items-center gap-3.5',
          'border-b border-[var(--ff-border)]/60 px-6 py-[11px]',
          selected
            ? 'bg-[var(--ff-surface-raised)] shadow-[inset_2px_0_0_var(--ff-select)]'
            : 'hover:bg-[var(--ff-surface)]',
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <Icon
            name={TYPE_ICON[form.type] ?? 'paragraph'}
            size={14}
            className="shrink-0 text-[var(--ff-text-faint)]"
          />
          <div className="min-w-0 flex-1">
            {renaming ? (
              // Renamed where it sits. The row's own click handlers are stopped here, or
              // clicking into the field would also be selecting the row underneath it.
              <form
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onSubmit={(e) => {
                  e.preventDefault();
                  const input = e.currentTarget.elements.namedItem('formName') as HTMLInputElement;
                  onRenamed(input.value);
                }}
              >
                <input
                  name="formName"
                  defaultValue={form.name}
                  autoFocus
                  aria-label={`Rename ${form.name}`}
                  onFocus={(e) => e.currentTarget.select()}
                  onBlur={(e) => onRenamed(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Escape') onCancelRename();
                  }}
                  className="w-full rounded-md border border-[var(--ff-primary)] bg-transparent
                             px-2 py-1 text-sm font-medium outline-none"
                />
              </form>
            ) : (
              <>
                <div className="truncate font-medium">{form.name}</div>
                <div className="truncate text-[12.5px] text-[var(--ff-text-faint)]">
                  {form.description || form.type}
                </div>
              </>
            )}
          </div>
        </div>

        <div className={clsx(
          'ff-num hidden text-[13.5px] sm:block',
          form.questionCount === 0 && 'text-[var(--ff-text-faint)]',
        )}>
          {form.questionCount}
        </div>

        <div className="flex items-center gap-2.5">
          <span className={clsx(
            'ff-num text-[13.5px]',
            form.responseCount === 0 && 'text-[var(--ff-text-faint)]',
          )}>
            {form.responseCount}
          </span>
          <Sparkline values={form.recent ?? []} />
        </div>

        <div>
          <span className={clsx(
            'inline-block rounded-full border px-2 py-[2px] text-[12px]',
            live
              ? 'border-[var(--ff-success)]/35 text-[var(--ff-success)]'
              : 'border-[var(--ff-accent)]/35 text-[var(--ff-accent)]',
          )}>
            {live ? 'Live' : form.status}
          </span>
        </div>

        <div className="hidden text-[12.5px] text-[var(--ff-text-faint)] sm:block">
          {relative(form.modifiedUtc ?? form.createdUtc)}
        </div>

        {/*
          The menu stops its own clicks. Without that, opening it would also select the row
          and a double-click on it would open the editor behind the menu.
        */}
        <div
          className="justify-self-end opacity-0 transition-opacity focus-within:opacity-100
                     group-hover/row:opacity-100 data-[open=true]:opacity-100"
          data-open={selected}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <Menu label={`Actions for ${form.name}`} width="w-52" trigger={
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md
                             text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-sunken)]
                             hover:text-[var(--ff-text)]">
              <Icon name="more" size={16} />
            </span>
          }>
            {(close) => (
              <>
                <MenuItem icon="pencil" onClick={() => { close(); onOpen(); }}>
                  Edit form
                </MenuItem>
                <MenuItem icon="text" onClick={() => { close(); onRename(); }}>
                  Rename
                </MenuItem>
                <MenuItem icon="copy" onClick={() => { close(); onDuplicate(); }}>
                  Duplicate
                </MenuItem>
                <MenuSeparator />
                <MenuItem icon="trash" tone="danger" onClick={() => { close(); onDelete(); }}>
                  Delete
                </MenuItem>
              </>
            )}
          </Menu>
        </div>
      </div>
    </li>
  );
}

/**
 * Seven days of response counts, as bars.
 *
 * Drawn only when the week actually had a response. A row of flat stubs on every form
 * would read as a chart that is telling you something, and it would be telling you
 * nothing — the count beside it already says zero.
 */
function Sparkline({ values }: { values: number[] }) {
  const peak = Math.max(...values, 0);
  if (peak === 0) return null;

  const total = values.reduce((sum, v) => sum + v, 0);

  return (
    <span
      className="flex h-4 items-end gap-[2px]"
      role="img"
      aria-label={`${total} response${total === 1 ? '' : 's'} in the last 7 days`}
    >
      {values.map((value, i) => (
        <span
          key={i}
          className={clsx(
            'w-[4px] rounded-[1px]',
            value > 0 ? 'bg-[var(--ff-select)]' : 'bg-[var(--ff-border-strong)]',
          )}
          style={{ height: value > 0 ? `${Math.max(3, (value / peak) * 16)}px` : '3px' }}
        />
      ))}
    </span>
  );
}

function DetailPane({
  form, folders, onFile,
}: {
  form: FormSummary;
  folders: { id: string; name: string }[];
  onFile: (projectId: string | null) => void;
}) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const shareUrl = `${location.origin}/f/${form.id}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* denied clipboard permission; the link is on screen to copy by hand */ }
  };

  return (
    <aside
      aria-label={`Details for ${form.name}`}
      className="hidden w-[288px] shrink-0 overflow-auto border-l border-[var(--ff-border)]
                 bg-[var(--ff-surface)] px-[18px] pb-8 pt-[18px] xl:block"
    >
      <h2 className="text-base font-semibold leading-snug">{form.name}</h2>
      <p className="mb-4 mt-0.5 text-[12.5px] text-[var(--ff-text-faint)]">
        {folders.find((f) => f.id === form.projectId)?.name ?? 'Unfiled'}
      </p>

      <Row label="Status" value={form.status === 'Published' ? 'Live' : form.status} />
      <Row label="Type" value={form.type} />
      <Row label="Questions" value={String(form.questionCount)} />
      <Row label="Responses" value={String(form.responseCount)} />
      <Row label="Created" value={new Date(form.createdUtc).toLocaleDateString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric',
      })} />
      <Row label="Last edited" value={relative(form.modifiedUtc ?? form.createdUtc)} />
      {form.lastResponseUtc && (
        <Row label="Last response" value={relative(form.lastResponseUtc)} />
      )}

      {/* Only for a form somebody can actually open — a draft has no published version
          behind that address, and offering the link would be offering a 404. */}
      {form.publishedVersionId && (
        <p className="ff-mono mt-4 break-all rounded-md border border-[var(--ff-border)]
                      bg-[var(--ff-surface-sunken)] px-2.5 py-2 text-[12.5px]
                      text-[var(--ff-select)]">
          {shareUrl}
        </p>
      )}

      <div className="mt-4 flex flex-col gap-2">
        <Action label="Open editor" hint="↵" onClick={() => navigate(`/forms/${form.id}`)} />
        <Action label="View responses" onClick={() => navigate(`/forms/${form.id}/responses`)} />
        <Action label="Share & embed" onClick={() => navigate(`/forms/${form.id}/share`)} />
        {form.publishedVersionId && (
          <Action label={copied ? 'Copied' : 'Copy share link'} onClick={copy} />
        )}
      </div>

      <div className="mt-4">
        <Field label="Folder">
          <Select
            value={form.projectId ?? ''}
            onChange={(e) => onFile(e.target.value || null)}
          >
            <option value="">Unfiled</option>
            {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </Select>
        </Field>
      </div>
    </aside>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--ff-border)]
                    py-2 text-[13.5px]">
      <span className="text-[var(--ff-text-muted)]">{label}</span>
      <b className="ff-num truncate font-medium">{value}</b>
    </div>
  );
}

function Action({
  label, hint, onClick,
}: { label: string; hint?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-between rounded-md border border-[var(--ff-border)]
                 bg-[var(--ff-surface-raised)] px-2.5 py-2 text-left text-[13.5px]
                 hover:border-[var(--ff-border-strong)]"
    >
      {label}
      {hint && (
        <kbd className="ff-mono rounded border border-[var(--ff-border)]
                        bg-[var(--ff-surface-sunken)] px-1.5 text-[11.5px]
                        text-[var(--ff-text-faint)]">
          {hint}
        </kbd>
      )}
    </button>
  );
}

function CreateFormCard({
  busy, onCreate, onCancel,
}: {
  busy: boolean;
  onCreate: (name: string, type: number, workspaceId?: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState('Survey');
  const [workspaceId, setWorkspaceId] = useState('');

  // Scoped to the company the header switcher is on. Without this the switcher only
  // changed what you saw: every new form landed in the tenant's default workspace
  // whichever company was selected.
  const { active } = useActiveCompany();

  const workspaces = useQuery({
    queryKey: ['workspaces', active?.id ?? 'all'],
    queryFn: () => api.workspaces.list(active?.id),
    enabled: Boolean(active),
  });

  const options = workspaces.data?.data ?? [];

  // Default to the company's own default workspace rather than leaving it blank, so the
  // common case needs no decision.
  const selected = workspaceId || options.find((w) => w.isDefault)?.id || options[0]?.id || '';

  return (
    <Card className="p-4">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) onCreate(name.trim(), TYPE_VALUES[type] ?? 1, selected || undefined);
        }}
      >
        <div className="min-w-52 flex-1">
          <Field label="Form name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Customer Experience 2026"
              autoFocus
              required
            />
          </Field>
        </div>
        <div className="w-40">
          <Field label="Type">
            <Select value={type} onChange={(e) => setType(e.target.value)}>
              {Object.keys(TYPE_VALUES).map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </Field>
        </div>

        {/* Only worth showing when there is a choice to make. */}
        {options.length > 1 && (
          <div className="w-52">
            <Field label="Workspace">
              <Select value={selected} onChange={(e) => setWorkspaceId(e.target.value)}>
                {options.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </Select>
            </Field>
          </div>
        )}

        <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      </form>
    </Card>
  );
}
